import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  AutoFixHigh as AutoIcon,
  Close as CloseIcon,
  DragIndicator as DragIcon,
  Map as MapIcon,
  Remove as RemoveIcon,
  Route as RouteIcon,
  Search as SearchIcon,
  TouchApp as ManualIcon,
  TwoWheeler as RiderIcon,
} from '@mui/icons-material';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GoogleMap, useJsApiLoader, Marker, InfoWindow } from '@react-google-maps/api';
import { getOrderMapLoaderOptions } from '../../config/googleMapsLoader';
import useRouteBuilder from '../../hooks/useRouteBuilder';
import useMultiRouteBuilder from '../../hooks/useMultiRouteBuilder';
import AutoRoutePanel from './AutoRoutePanel';
import MultiRiderAssign from './MultiRiderAssign';
import {
  MAP_ORDER_CAP,
  MAP_ORDER_STATUSES,
  ORDERS_FETCH_LIMIT,
  formatDistance,
  formatDuration,
  getOrderId,
  getRouteColor,
  isValidDeliveryDate,
  isValidLatLng,
  ROUTES_API_MAX_INTERMEDIATES,
} from '../../constants/routePlanning';
import { useApp } from '../../context/AppContext';
import api from '../../utils/api';
import { cacheEndpoints } from '../../utils/apiCache';
import { appConfig } from '../../config/app';

const mapContainerStyle = { width: '100%', height: '100%' };
const defaultCenter = { lat: 28.6139, lng: 77.209 };

const MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
];

const PIN_PLOTTED = 'http://maps.google.com/mapfiles/ms/icons/green-dot.png';
const PIN_IN_ROUTE = 'http://maps.google.com/mapfiles/ms/icons/blue-dot.png';
const PIN_STORE = 'http://maps.google.com/mapfiles/ms/icons/red-dot.png';

/** Stores persist coordinates as "lat, lng"; some payloads also carry latLong. */
const parseStorePosition = (store) => {
  if (isValidLatLng(store?.latLong)) return store.latLong;
  if (typeof store?.location !== 'string' || !store.location.includes(',')) return null;
  const [latRaw, lngRaw] = store.location.split(',');
  const pos = { lat: parseFloat(latRaw), lng: parseFloat(lngRaw) };
  return isValidLatLng(pos) ? pos : null;
};

const getOrderStoreId = (order) =>
  order?.storeId ||
  order?.branch_id ||
  order?.storeDetails?.branchId ||
  order?.storeDetails?.id ||
  null;

/** Routable statuses only — default both; user may narrow to one. */
const STATUS_BOTH = 'routable';
const ROUTABLE_STATUS_OPTIONS = [
  { value: STATUS_BOTH, label: 'Pending + Confirmed' },
  { value: 'pending', label: 'Pending' },
  { value: 'confirmed', label: 'Confirmed' },
];

const getCustomerName = (order) =>
  order?.deliveryAddress?.userName ||
  order?.customerName ||
  order?.customer_name ||
  'Customer';

const getSlotLabel = (order) => {
  if (order?.timeSlotDetails?.displayTime) return order.timeSlotDetails.displayTime;
  if (order?.timeSlotDetails?.startTime && order?.timeSlotDetails?.endTime) {
    return `${order.timeSlotDetails.startTime} - ${order.timeSlotDetails.endTime}`;
  }
  return 'No slot';
};

const SortableRouteRow = ({ order, index, onRemove }) => {
  const id = getOrderId(order);
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id,
  });

  return (
    <ListItem
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.7 : 1,
        background: '#fff',
        borderBottom: '1px solid #eee',
      }}
      secondaryAction={
        <IconButton edge="end" size="small" onClick={() => onRemove(id)} aria-label="Remove from route">
          <RemoveIcon fontSize="small" />
        </IconButton>
      }
      sx={{ pr: 6 }}
    >
      <ListItemIcon sx={{ minWidth: 36 }} {...attributes} {...listeners}>
        <DragIcon fontSize="small" color="action" sx={{ cursor: 'grab' }} />
      </ListItemIcon>
      <ListItemText
        primary={`${index + 1}. #${id}`}
        secondary={`${getCustomerName(order)} · ${getSlotLabel(order)}`}
        primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
        secondaryTypographyProps={{ variant: 'caption' }}
      />
    </ListItem>
  );
};

/**
 * Self-contained 3-step route planner modal (REV-2).
 * Step 1 filter → Step 2 map/build → Step 3 rider assign.
 * Deleting this component + its OrderList button removes the feature entirely.
 */
/**
 * @param {object}  props
 * @param {string}  [props.fixedStoreId]    when set, the store is locked to this branch id
 *                                          (store admin) and the Store dropdown is read-only.
 * @param {string}  [props.fixedStoreName]  label shown in place of the dropdown.
 */
const OrderMapSelector = ({
  open,
  onClose,
  onOrdersChanged,
  fixedStoreId = null,
  fixedStoreName = '',
}) => {
  const { actions } = useApp();
  const [step, setStep] = useState('filter'); // 'filter' | 'map' | 'rider'
  const [mode, setMode] = useState('manual'); // 'manual' (single route) | 'auto' (multi-rider)

  const route = useRouteBuilder({
    onMessage: (message, severity) => actions.showSnackbar(message, severity),
    autoEstimate: step === 'map' && mode === 'manual',
  });

  const multiRoute = useMultiRouteBuilder({
    onMessage: (message, severity) => actions.showSnackbar(message, severity),
  });

  const [failedRoutes, setFailedRoutes] = useState([]);

  // --- Step 1: filter ---
  const [deliveryDate, setDeliveryDate] = useState('');
  const [storeFilter, setStoreFilter] = useState(fixedStoreId || 'all');
  const [timeSlotFilter, setTimeSlotFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(STATUS_BOTH);
  const [stores, setStores] = useState([]);
  const [timeSlots, setTimeSlots] = useState([]);
  const [filterLoading, setFilterLoading] = useState(false);
  const [filterError, setFilterError] = useState('');
  const [mapNotice, setMapNotice] = useState('');
  const [mapOrders, setMapOrders] = useState([]);

  // --- Step 2: map ---
  const { isLoaded, loadError } = useJsApiLoader(getOrderMapLoaderOptions());
  const [map, setMap] = useState(null);
  const [activeMarkerId, setActiveMarkerId] = useState(null);
  const [activeStoreId, setActiveStoreId] = useState(null);

  // --- Step 3: rider ---
  const [createdRoute, setCreatedRoute] = useState(null);
  const [riderSearch, setRiderSearch] = useState('');
  const [riders, setRiders] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [selectedRider, setSelectedRider] = useState(null);
  const [allocating, setAllocating] = useState(false);
  const [riderError, setRiderError] = useState('');

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const resetAll = useCallback(() => {
    setStep('filter');
    setMode('manual');
    setFailedRoutes([]);
    setDeliveryDate('');
    setStoreFilter(fixedStoreId || 'all');
    setTimeSlotFilter('all');
    setStatusFilter(STATUS_BOTH);
    setFilterLoading(false);
    setFilterError('');
    setMapNotice('');
    setMapOrders([]);
    setMap(null);
    setActiveMarkerId(null);
    setActiveStoreId(null);
    setCreatedRoute(null);
    setRiderSearch('');
    setRiders([]);
    setSelectedRider(null);
    setAllocating(false);
    setRiderError('');
    route.reset();
    multiRoute.reset();
  }, [route, multiRoute, fixedStoreId]);

  /**
   * The parent resolves its store asynchronously, so fixedStoreId is usually null on first
   * mount and arrives later. Without this, storeFilter stays on its initial 'all' and the
   * locked Select — which has no 'all' option — renders blank.
   */
  useEffect(() => {
    if (fixedStoreId) {
      setStoreFilter(fixedStoreId);
    }
  }, [fixedStoreId]);

  useEffect(() => {
    if (!open) {
      resetAll();
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const [storesData, slotsRes] = await Promise.all([
          cacheEndpoints.stores.getAll(),
          api.get('/time-slots'),
        ]);
        if (cancelled) return;
        setStores(storesData || []);
        if (slotsRes.data?.success) {
          setTimeSlots(slotsRes.data.timeSlots || []);
        }
      } catch (err) {
        console.error('Failed to load filter options', err);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  /**
   * Params for one call to the existing GET /orders endpoint.
   * That endpoint filters status with a single equality match, so "Pending + Confirmed"
   * is issued as two calls and merged client-side rather than changing the endpoint.
   */
  const buildOrdersParams = (status) => {
    const params = {
      status,
      delivery_date: deliveryDate,
      limit: ORDERS_FETCH_LIMIT,
      page: 1,
    };
    if (storeFilter && storeFilter !== 'all') params.branch_id = storeFilter;
    if (timeSlotFilter && timeSlotFilter !== 'all') params.timeSlotId = timeSlotFilter;
    return params;
  };

  /** Which statuses this apply should fetch — one call each. */
  const statusesToFetch = () =>
    statusFilter === STATUS_BOTH ? [...MAP_ORDER_STATUSES] : [statusFilter];

  const statusLabel =
    ROUTABLE_STATUS_OPTIONS.find((o) => o.value === statusFilter)?.label ||
    'Pending + Confirmed';

  const loadFilteredOrders = async () => {
    const statuses = statusesToFetch();
    const responses = await Promise.all(
      statuses.map((status) => api.get('/orders', { params: buildOrdersParams(status) }))
    );

    const failed = responses.find((res) => !res.data?.success);
    if (failed) {
      return { error: failed.data?.message || 'Failed to load orders' };
    }

    const seen = new Set();
    const orders = [];
    responses.forEach((res) => {
      (res.data.orders || []).forEach((order) => {
        const id = order.orderId || order.id;
        if (!id || seen.has(id)) return;
        // A created route keeps the order pending until a rider is assigned, but it
        // already has a routeId and must not be planned again.
        if (order.routeId) return;
        seen.add(id);
        orders.push(order);
      });
    });
    return { orders };
  };

  const showOrdersOnMap = (orders, notice = '') => {
    setMapOrders(orders);
    route.initFromOrders(orders);
    multiRoute.initFromOrders(orders);
    setFailedRoutes([]);
    setActiveMarkerId(null);
    setActiveStoreId(null);
    setCreatedRoute(null);
    setRiderSearch('');
    setRiders([]);
    setSelectedRider(null);
    setRiderError('');
    setMapNotice(notice);
    setStep('map');
  };

  const handleApplyFilters = async () => {
    if (!deliveryDate) {
      setFilterError('Delivery date is required');
      return;
    }
    if (!isValidDeliveryDate(deliveryDate)) {
      setFilterError('Delivery date must be a valid YYYY-MM-DD date');
      return;
    }

    setFilterLoading(true);
    setFilterError('');
    setMapNotice('');
    try {
      const result = await loadFilteredOrders();
      if (result.error) {
        setFilterError(result.error);
        return;
      }

      const orders = result.orders || [];
      if (orders.length === 0) {
        setFilterError(`No ${statusLabel.toLowerCase()} orders for this delivery date`);
        return;
      }

      if (orders.length > MAP_ORDER_CAP) {
        setFilterError(
          `${orders.length} ${statusLabel.toLowerCase()} orders match (cap is ${MAP_ORDER_CAP}). Narrow store, time slot, or status and try again.`
        );
        return;
      }

      showOrdersOnMap(orders);
    } catch (error) {
      const data = error.response?.data || {};
      setFilterError(data.message || error.message || 'Failed to load orders');
    } finally {
      setFilterLoading(false);
    }
  };

  /** Same filter as step 1, after a route is saved. Stays on the map. */
  const refreshRemainingOrders = async () => {
    setFilterLoading(true);
    try {
      const result = await loadFilteredOrders();
      if (result.error) {
        setFilterError(result.error);
        setStep('filter');
        return;
      }

      const orders = result.orders || [];
      if (orders.length > MAP_ORDER_CAP) {
        setFilterError(
          `${orders.length} ${statusLabel.toLowerCase()} orders match (cap is ${MAP_ORDER_CAP}). Narrow store, time slot, or status and try again.`
        );
        setStep('filter');
        return;
      }

      if (orders.length === 0) {
        showOrdersOnMap(
          [],
          'Every order in this filter is now on a route. Change the filter or close when you are done.'
        );
        return;
      }

      showOrdersOnMap(orders, `${orders.length} order${orders.length === 1 ? '' : 's'} still available for the next route.`);
    } catch (error) {
      const data = error.response?.data || {};
      setFilterError(data.message || error.message || 'Failed to refresh orders');
      setStep('filter');
    } finally {
      setFilterLoading(false);
    }
  };

  const plottableOrders = useMemo(
    () => mapOrders.filter((order) => isValidLatLng(order.deliveryAddress?.latLong)),
    [mapOrders]
  );

  // Auto plan needs one origin store for all routes. This is a UX gate; the backend is the
  // real guard (rejects with MULTI_STORE_NOT_SUPPORTED).
  const distinctStoreIds = useMemo(() => {
    const set = new Set();
    mapOrders.forEach((order) => {
      const id = getOrderStoreId(order);
      if (id) set.add(String(id));
    });
    return Array.from(set);
  }, [mapOrders]);
  const autoMultiStore = distinctStoreIds.length > 1;

  const storePins = useMemo(() => {
    const wanted = new Set();
    if (storeFilter && storeFilter !== 'all') wanted.add(String(storeFilter));
    if (fixedStoreId) wanted.add(String(fixedStoreId));
    if (route.originStoreId) wanted.add(String(route.originStoreId));
    mapOrders.forEach((order) => {
      const id = getOrderStoreId(order);
      if (id) wanted.add(String(id));
    });

    if (wanted.size === 0) return [];

    const pins = [];
    stores.forEach((store) => {
      const ids = [store.branchId, store.id].filter(Boolean).map(String);
      if (!ids.some((id) => wanted.has(id))) return;
      const position = parseStorePosition(store);
      if (!position) return;
      pins.push({
        id: String(store.branchId || store.id),
        name: store.storeName || store.name || 'Store',
        position,
      });
    });
    return pins;
  }, [stores, mapOrders, storeFilter, fixedStoreId, route.originStoreId]);

  const fitBounds = useCallback(
    (mapInstance) => {
      if (!mapInstance || !window.google) return;
      if (plottableOrders.length === 0 && storePins.length === 0) return;
      const bounds = new window.google.maps.LatLngBounds();
      plottableOrders.forEach((order) => {
        const { lat, lng } = order.deliveryAddress.latLong;
        bounds.extend({ lat, lng });
      });
      storePins.forEach((store) => bounds.extend(store.position));
      mapInstance.fitBounds(bounds, 64);
    },
    [plottableOrders, storePins]
  );

  const onMapLoad = useCallback(
    (mapInstance) => {
      setMap(mapInstance);
      fitBounds(mapInstance);
    },
    [fitBounds]
  );

  useEffect(() => {
    if (step === 'map' && map) fitBounds(map);
  }, [step, map, fitBounds]);

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (over) route.reorder(active.id, over.id);
  };

  const handleCreateRoute = async () => {
    const created = await route.createRoute();
    if (created) {
      setCreatedRoute(created);
      setRiderSearch('');
      setRiders([]);
      setSelectedRider(null);
      setRiderError('');
      setStep('rider');
    }
  };

  const handleCreateAllRoutes = async () => {
    const { created, failed } = await multiRoute.createAll();
    if (created.length > 0) {
      setFailedRoutes(failed);
      if (typeof onOrdersChanged === 'function') onOrdersChanged();
      setStep('rider');
    }
  };

  /** Auto-mode step 3 finished: refresh the parent and close the planner. */
  const handleMultiRiderDone = () => {
    if (typeof onOrdersChanged === 'function') onOrdersChanged();
    onClose();
  };

  const handleSearchRiders = async () => {
    const search = (riderSearch || '').trim();
    if (!search) {
      actions.showSnackbar('Enter name or mobile number to search', 'warning');
      return;
    }
    setSearchLoading(true);
    setRiderError('');
    setRiders([]);
    try {
      const response = await fetch(
        `${appConfig.api.baseURL}/users/riders/list?search=${encodeURIComponent(search)}`,
        {
          headers: {
            Authorization: `Bearer ${localStorage.getItem(appConfig.storage.token)}`,
          },
        }
      );
      if (!response.ok) throw new Error('Failed to fetch riders');
      const data = await response.json();
      if (data.success) {
        setRiders(data.riders || []);
        if (!(data.riders || []).length) {
          actions.showSnackbar('No riders found', 'info');
        }
      } else {
        setRiderError(data.message || 'Search failed');
        actions.showSnackbar(data.message || 'Search failed', 'error');
      }
    } catch (err) {
      console.error('Error searching riders:', err);
      setRiderError(err.message || 'Failed to search riders');
      actions.showSnackbar(err.message || 'Failed to search riders', 'error');
    } finally {
      setSearchLoading(false);
    }
  };

  const handleAllocateRider = async () => {
    if (!selectedRider) {
      actions.showSnackbar('Please select a rider', 'warning');
      return;
    }
    if (!createdRoute) {
      actions.showSnackbar('No route created for allocation', 'warning');
      return;
    }

    setAllocating(true);
    try {
      const response = await fetch(`${appConfig.api.baseURL}/orders/bulk-update-orders`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${localStorage.getItem(appConfig.storage.token)}`,
        },
        body: JSON.stringify({
          orderIds: route.orderedIds,
          updateData: {
            deliveryPersonMobile: selectedRider.userId,
            deliveryPersonName: selectedRider.f_name,
            deliveryFrom: 'PILOT',
            routeId: createdRoute.routeId,
            order_status: 'assigned',
          },
        }),
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${errorText}`);
      }

      const data = await response.json();
      if (!data.success) {
        actions.showSnackbar(data.message || 'Failed to allocate rider', 'error');
        return;
      }

      try {
        await fetch(`${appConfig.api.baseURL}/delivery-routes/${createdRoute.routeId}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${localStorage.getItem(appConfig.storage.token)}`,
          },
          body: JSON.stringify({
            updateData: {
              deliveryPersonId: selectedRider.userId,
              deliveryPersonName: selectedRider.f_name,
              deliveryPersonMobile: selectedRider.mobileNo,
            },
          }),
        });
      } catch (routeError) {
        console.warn('Error updating route with delivery person details:', routeError);
      }

      actions.showSnackbar(
        `Route assigned to ${selectedRider.f_name}. Remaining orders are back on the map.`,
        'success'
      );
      if (typeof onOrdersChanged === 'function') {
        onOrdersChanged();
      }
      await refreshRemainingOrders();
    } catch (error) {
      console.error('Error allocating rider:', error);
      actions.showSnackbar('Error allocating rider', 'error');
    } finally {
      setAllocating(false);
    }
  };

  const handleClose = () => {
    if (route.creating || multiRoute.creating || multiRoute.planning || allocating || filterLoading) return;
    onClose();
  };

  const activeOrder = activeMarkerId
    ? route.ordersById[activeMarkerId] ||
      plottableOrders.find((o) => getOrderId(o) === activeMarkerId)
    : null;

  const activeStore = activeStoreId
    ? storePins.find((store) => store.id === activeStoreId) || null
    : null;

  const autoPinCaption = (() => {
    if (!activeOrder) return '';
    const assignedId = multiRoute.routeOfOrder(getOrderId(activeOrder));
    if (assignedId == null) {
      return multiRoute.routes.length === 0
        ? 'Run Auto Plan to split these orders into routes.'
        : 'Not on a route.';
    }
    const routeIndex = multiRoute.routes.findIndex((r) => r.id === assignedId);
    const seq = multiRoute.sequenceIndex(getOrderId(activeOrder));
    return `Route ${routeIndex + 1}, stop ${seq}. Drag the list to change order.`;
  })();

  const stepTitle =
    step === 'filter'
      ? 'Plan Route on Map'
      : step === 'map'
        ? 'Build route on map'
        : mode === 'auto'
          ? 'Assign riders to routes'
          : 'Assign rider to route';

  const inRouteCount = mode === 'auto' ? multiRoute.totals.routed : route.orderedIds.length;

  const stepSubtitle =
    step === 'filter'
      ? 'Choose a delivery date, then optionally narrow by status, store, or time slot'
      : step === 'map'
        ? `${plottableOrders.length} plottable · ${inRouteCount} in route${
            mode === 'auto' ? ` · ${multiRoute.routes.length} route(s)` : ''
          }${
            route.missingLocationOrders.length > 0
              ? ` · ${route.missingLocationOrders.length} without location`
              : ''
          }`
        : mode === 'auto'
          ? `${multiRoute.createdRoutes.length} route(s) created`
          : `Route ${createdRoute?.routeId || ''} · ${route.orderedIds.length} order(s)`;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={step === 'map'}
      maxWidth={step === 'map' ? false : 'md'}
      fullWidth
      PaperProps={{
        sx: step === 'map' ? undefined : { minHeight: 480 },
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 1 }}>
        {step === 'rider' ? <RiderIcon color="primary" /> : <MapIcon color="primary" />}
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" component="span">
            {stepTitle}
          </Typography>
          <Typography variant="body2" color="text.secondary" display="block">
            {stepSubtitle}
          </Typography>
        </Box>
        <IconButton
          onClick={handleClose}
          aria-label="Close"
          disabled={route.creating || multiRoute.creating || allocating}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      {/* ——— Step 1: Filter ——— */}
      {step === 'filter' && (
        <>
          <DialogContent>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
              <TextField
                label="Delivery Date"
                type="date"
                value={deliveryDate}
                onChange={(e) => {
                  setDeliveryDate(e.target.value);
                  setFilterError('');
                }}
                InputLabelProps={{ shrink: true }}
                helperText="Required. Only routable (pending/confirmed) orders for this day."
                error={Boolean(deliveryDate) && !isValidDeliveryDate(deliveryDate)}
                fullWidth
                required
              />
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => {
                    setStatusFilter(e.target.value);
                    setFilterError('');
                  }}
                >
                  {ROUTABLE_STATUS_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Store</InputLabel>
                <Select
                  value={storeFilter}
                  label="Store"
                  onChange={(e) => setStoreFilter(e.target.value)}
                  disabled={Boolean(fixedStoreId)}
                  // Keep the locked store name fully legible — MUI dims disabled text to 38%.
                  sx={
                    fixedStoreId
                      ? {
                          '& .MuiSelect-select.Mui-disabled': {
                            WebkitTextFillColor: 'rgba(0, 0, 0, 0.87)',
                            color: 'rgba(0, 0, 0, 0.87)',
                          },
                        }
                      : undefined
                  }
                >
                  {/* Store admin: locked to their own store, no "All Stores" option. */}
                  {fixedStoreId ? (
                    <MenuItem value={fixedStoreId}>
                      {fixedStoreName ||
                        stores.find((s) => s.branchId === fixedStoreId)?.storeName ||
                        fixedStoreId}
                    </MenuItem>
                  ) : (
                    [
                      <MenuItem key="all" value="all">
                        All Stores
                      </MenuItem>,
                      ...stores.map((store) => (
                        <MenuItem key={store.branchId} value={store.branchId}>
                          {store.storeName}
                        </MenuItem>
                      )),
                    ]
                  )}
                </Select>
              </FormControl>
              <FormControl fullWidth>
                <InputLabel>Time Slot</InputLabel>
                <Select
                  value={timeSlotFilter}
                  label="Time Slot"
                  onChange={(e) => setTimeSlotFilter(e.target.value)}
                >
                  <MenuItem value="all">All Time Slots</MenuItem>
                  {timeSlots.map((slot) => (
                    <MenuItem key={slot.id} value={slot.id}>
                      {slot.startTime && slot.endTime
                        ? `${slot.startTime} - ${slot.endTime}`
                        : slot.id}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {filterError && <Alert severity="warning">{filterError}</Alert>}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} disabled={filterLoading}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleApplyFilters}
              disabled={filterLoading || !deliveryDate}
              startIcon={filterLoading ? <CircularProgress size={18} color="inherit" /> : null}
            >
              {filterLoading ? 'Checking…' : 'Apply'}
            </Button>
          </DialogActions>
        </>
      )}

      {/* ——— Step 2: Map + build ——— */}
      {step === 'map' && (
        <>
          <DialogContent sx={{ p: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {mapNotice && (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                {mapNotice}
              </Alert>
            )}

            {route.missingLocationOrders.length > 0 && (
              <Alert severity="warning" sx={{ borderRadius: 0 }}>
                {route.missingLocationOrders.length} order
                {route.missingLocationOrders.length === 1 ? ' has' : 's have'} no location and
                {route.missingLocationOrders.length === 1 ? ' was' : ' were'} excluded from the map:{' '}
                {route.missingLocationOrders
                  .map((o) => `#${getOrderId(o)}`)
                  .slice(0, 8)
                  .join(', ')}
                {route.missingLocationOrders.length > 8
                  ? ` +${route.missingLocationOrders.length - 8} more`
                  : ''}
              </Alert>
            )}

            {route.multiStore && (
              <Alert severity="info" sx={{ borderRadius: 0 }}>
                Selected orders span multiple stores. Origin store for ETA is{' '}
                {route.originStoreId || 'the first resolvable store'} — confirm before creating.
              </Alert>
            )}

            <Box sx={{ flex: 1, display: 'flex', minHeight: 0 }}>
              <Box sx={{ flex: 1, position: 'relative', minWidth: 0 }}>
                {loadError && (
                  <Alert severity="error" sx={{ m: 2 }}>
                    Failed to load Google Maps
                  </Alert>
                )}
                {!isLoaded && !loadError && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                    <CircularProgress />
                  </Box>
                )}
                {isLoaded && (
                  <GoogleMap
                    mapContainerStyle={mapContainerStyle}
                    center={defaultCenter}
                    zoom={11}
                    onLoad={onMapLoad}
                    options={{
                      streetViewControl: false,
                      mapTypeControl: false,
                      fullscreenControl: false,
                      zoomControl: true,
                      zoomControlOptions: {
                        position: window.google.maps.ControlPosition.RIGHT_BOTTOM,
                      },
                      styles: MAP_STYLES,
                      clickableIcons: false,
                    }}
                  >
                    {storePins.map((store) => (
                      <Marker
                        key={`store-${store.id}`}
                        position={store.position}
                        icon={PIN_STORE}
                        title={store.name}
                        zIndex={500}
                        onClick={() => {
                          setActiveStoreId(store.id);
                          setActiveMarkerId(null);
                        }}
                      />
                    ))}

                    {plottableOrders.map((order) => {
                      const id = getOrderId(order);
                      const { lat, lng } = order.deliveryAddress.latLong;

                      let icon = PIN_PLOTTED;
                      let label;
                      let zIndex = 1;

                      if (mode === 'auto') {
                        const assignedRouteId = multiRoute.routeOfOrder(id);
                        if (assignedRouteId != null) {
                          const r = multiRoute.routes.find((rt) => rt.id === assignedRouteId);
                          const seq = multiRoute.sequenceIndex(id);
                          icon = getRouteColor(r?.colorIndex ?? 0).pin;
                          label = {
                            text: String(seq),
                            color: '#ffffff',
                            fontWeight: '700',
                            fontSize: '12px',
                          };
                          zIndex = 1000 + (seq || 0);
                        }
                      } else {
                        const inRoute = route.isInRoute(id);
                        const seq = route.sequenceIndex(id);
                        icon = inRoute ? PIN_IN_ROUTE : PIN_PLOTTED;
                        label = inRoute
                          ? { text: String(seq), color: '#ffffff', fontWeight: '700', fontSize: '12px' }
                          : undefined;
                        zIndex = inRoute ? 1000 + (seq || 0) : 1;
                      }

                      return (
                        <Marker
                          key={id}
                          position={{ lat, lng }}
                          icon={icon}
                          label={label}
                          zIndex={zIndex}
                          onClick={() => {
                            if (mode === 'manual') {
                              if (route.isInRoute(id)) {
                                route.removeOrder(id);
                              } else {
                                route.addOrder(order);
                              }
                            }
                            // Auto mode: the pin only opens the InfoWindow; moving between
                            // routes happens via the InfoWindow's route selector.
                            setActiveMarkerId(id);
                            setActiveStoreId(null);
                          }}
                        />
                      );
                    })}

                    {activeStore && (
                      <InfoWindow
                        position={activeStore.position}
                        onCloseClick={() => setActiveStoreId(null)}
                      >
                        <Box sx={{ minWidth: 140, p: 0.5 }}>
                          <Typography variant="subtitle2">{activeStore.name}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            Store
                          </Typography>
                        </Box>
                      </InfoWindow>
                    )}

                    {activeOrder && isValidLatLng(activeOrder.deliveryAddress?.latLong) && (
                      <InfoWindow
                        position={{
                          lat: activeOrder.deliveryAddress.latLong.lat,
                          lng: activeOrder.deliveryAddress.latLong.lng,
                        }}
                        onCloseClick={() => setActiveMarkerId(null)}
                      >
                        <Box sx={{ minWidth: 180, p: 0.5 }}>
                          <Typography variant="subtitle2">#{getOrderId(activeOrder)}</Typography>
                          <Typography variant="body2">{getCustomerName(activeOrder)}</Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            {getSlotLabel(activeOrder)}
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                            {route.isInRoute(getOrderId(activeOrder))
                              ? `In route (#${route.sequenceIndex(getOrderId(activeOrder))}) — click pin to remove`
                              : 'Click pin to add to route'}
                          </Typography>
                        </Box>
                      </InfoWindow>
                    )}
                  </GoogleMap>
                )}
              </Box>

              <Paper
                elevation={0}
                square
                sx={{
                  width: mode === 'auto' ? 420 : 360,
                  maxWidth: mode === 'auto' ? '42%' : '40%',
                  flexShrink: 0,
                  borderLeft: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ px: 2, pt: 1.5, pb: 1, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <ToggleButtonGroup
                    size="small"
                    exclusive
                    fullWidth
                    value={mode}
                    onChange={(e, next) => {
                      if (next) {
                        setMode(next);
                        setActiveMarkerId(null);
                      }
                    }}
                  >
                    <ToggleButton value="manual">
                      <ManualIcon fontSize="small" sx={{ mr: 0.5 }} /> Manual (1 route)
                    </ToggleButton>
                    <Tooltip
                      title={autoMultiStore ? 'Auto plan needs a single store — narrow the store filter' : ''}
                    >
                      <span style={{ width: '100%', display: 'flex' }}>
                        <ToggleButton value="auto" sx={{ flex: 1 }}>
                          <AutoIcon fontSize="small" sx={{ mr: 0.5 }} /> Auto (N riders)
                        </ToggleButton>
                      </span>
                    </Tooltip>
                  </ToggleButtonGroup>
                </Box>

                {mode === 'auto' && autoMultiStore && (
                  <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    These orders span {distinctStoreIds.length} stores. Auto plan needs a single
                    origin store — narrow the store filter to use it.
                  </Alert>
                )}

                {mode === 'auto' ? (
                  <AutoRoutePanel multiRoute={multiRoute} disabled={autoMultiStore} />
                ) : (
                  <>
                <Box sx={{ p: 2, borderBottom: '1px solid', borderColor: 'divider' }}>
                  <Typography variant="subtitle1" fontWeight={700}>
                    Route sequence
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={route.returnToStore}
                        onChange={(e) => route.setReturnToStore(e.target.checked)}
                        size="small"
                      />
                    }
                    label="Return to store"
                    sx={{ mt: 0.5, mb: 0 }}
                  />
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2">
                      Distance:{' '}
                      <strong>
                        {route.estimating ? '…' : formatDistance(route.totalDistanceMeters)}
                      </strong>
                    </Typography>
                    <Typography variant="body2">
                      ETA:{' '}
                      <strong>
                        {route.estimating ? '…' : formatDuration(route.totalDurationSeconds)}
                      </strong>
                    </Typography>
                    {route.estimateError && (
                      <Typography variant="caption" color="error" display="block">
                        {route.estimateError}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ flex: 1, overflow: 'auto' }}>
                  {route.orderedOrders.length === 0 ? (
                    <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
                      Route is empty. Click a pin on the map to add it.
                    </Typography>
                  ) : (
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragEnd={handleDragEnd}
                    >
                      <SortableContext
                        items={route.orderedIds}
                        strategy={verticalListSortingStrategy}
                      >
                        <List dense disablePadding>
                          {route.orderedOrders.map((order, index) => (
                            <SortableRouteRow
                              key={getOrderId(order)}
                              order={order}
                              index={index}
                              onRemove={route.removeOrder}
                            />
                          ))}
                        </List>
                      </SortableContext>
                    </DndContext>
                  )}
                </Box>

                {route.optimizeDisabled && route.optimizeDisabledReason && (
                  <Alert severity="warning" sx={{ borderRadius: 0 }}>
                    {route.optimizeDisabledReason}
                  </Alert>
                )}
                  </>
                )}
              </Paper>
            </Box>
          </DialogContent>

          <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
            <Button
              onClick={() => {
                setStep('filter');
                setMap(null);
                setActiveMarkerId(null);
                setActiveStoreId(null);
              }}
              disabled={route.creating}
              startIcon={<BackIcon />}
            >
              Back
            </Button>
            <Box sx={{ flex: 1 }} />
            <Button onClick={handleClose} disabled={route.creating || multiRoute.creating}>
              Cancel
            </Button>
            {mode === 'manual' ? (
              <>
                <Tooltip
                  title={
                    route.orderedIds.length > ROUTES_API_MAX_INTERMEDIATES
                      ? `Optimize supports at most ${ROUTES_API_MAX_INTERMEDIATES} stops`
                      : route.optimizeDisabledReason || ''
                  }
                >
                  <span>
                    <Button
                      variant="outlined"
                      onClick={route.optimize}
                      disabled={!route.canOptimize || route.optimizing || route.creating}
                      startIcon={route.optimizing ? <CircularProgress size={18} /> : <RouteIcon />}
                    >
                      {route.optimizing ? 'Optimizing…' : 'Optimize'}
                    </Button>
                  </span>
                </Tooltip>
                <Button
                  variant="contained"
                  onClick={handleCreateRoute}
                  disabled={route.orderedIds.length === 0 || route.creating}
                  startIcon={route.creating ? <CircularProgress size={18} /> : null}
                >
                  {route.creating ? 'Creating…' : `Create Route (${route.orderedIds.length})`}
                </Button>
              </>
            ) : (
              <Button
                variant="contained"
                onClick={handleCreateAllRoutes}
                disabled={multiRoute.routes.length === 0 || multiRoute.creating}
                startIcon={multiRoute.creating ? <CircularProgress size={18} /> : null}
              >
                {multiRoute.creating
                  ? 'Creating…'
                  : `Create ${multiRoute.routes.length} Route${multiRoute.routes.length === 1 ? '' : 's'}`}
              </Button>
            )}
          </DialogActions>
        </>
      )}

      {/* ——— Step 3 (Auto): one rider per created route ——— */}
      {step === 'rider' && mode === 'auto' && (
        <DialogContent>
          <MultiRiderAssign
            createdRoutes={multiRoute.createdRoutes}
            failedRoutes={failedRoutes}
            onDone={handleMultiRiderDone}
          />
        </DialogContent>
      )}

      {/* ——— Step 3 (Manual): Rider ——— */}
      {step === 'rider' && mode === 'manual' && (
        <>
          <DialogContent>
            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
              <Typography variant="subtitle2" gutterBottom>
                Route: {createdRoute?.routeId}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {route.orderedIds.map((orderId, index) => (
                  <Typography
                    key={orderId}
                    component="span"
                    variant="caption"
                    sx={{
                      px: 1,
                      py: 0.25,
                      borderRadius: 1,
                      bgcolor: 'primary.main',
                      color: 'primary.contrastText',
                    }}
                  >
                    {index + 1}. #{orderId}
                  </Typography>
                ))}
              </Box>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Search by rider name or mobile number, then select from results. After assign, this
              planner stays open and reloads the remaining orders for the same filter.
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, mb: 2 }}>
              <TextField
                fullWidth
                size="small"
                placeholder="Name or mobile number"
                value={riderSearch}
                onChange={(e) => setRiderSearch(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSearchRiders()}
                disabled={searchLoading || allocating}
              />
              <Button
                variant="outlined"
                onClick={handleSearchRiders}
                disabled={searchLoading || allocating}
                startIcon={searchLoading ? <CircularProgress size={18} /> : <SearchIcon />}
              >
                {searchLoading ? 'Searching…' : 'Search'}
              </Button>
            </Box>

            {riderError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {riderError}
              </Alert>
            )}

            <Box sx={{ maxHeight: 280, overflow: 'auto' }}>
              {!searchLoading && !riderSearch.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  Search for a rider first.
                </Typography>
              )}
              {!searchLoading && riders.length === 0 && riderSearch.trim() && (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                  No riders found. Try another search.
                </Typography>
              )}
              {!searchLoading &&
                riders.map((rider) => {
                  const isSelected = selectedRider?.userId === rider.userId;
                  return (
                    <Box
                      key={rider.userId}
                      onClick={() => setSelectedRider(rider)}
                      sx={{
                        py: 1.5,
                        px: 2,
                        mb: 0.5,
                        cursor: 'pointer',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: isSelected ? 'primary.main' : 'divider',
                        backgroundColor: isSelected ? 'action.selected' : 'transparent',
                        '&:hover': {
                          backgroundColor: isSelected ? 'action.selected' : 'action.hover',
                        },
                      }}
                    >
                      <Typography variant="body1">
                        {rider.f_name} {rider.l_name || ''} — {rider.mobileNo}
                      </Typography>
                      {rider.storeDetails && (
                        <Typography variant="caption" color="text.secondary" display="block">
                          {rider.storeDetails.name || rider.storeDetails.storeName}
                        </Typography>
                      )}
                    </Box>
                  );
                })}
            </Box>

            {selectedRider && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Selected: {selectedRider.f_name} {selectedRider.l_name || ''} ({selectedRider.mobileNo})
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={handleClose} disabled={allocating}>
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleAllocateRider}
              disabled={!selectedRider || allocating || !createdRoute}
              startIcon={allocating ? <CircularProgress size={18} /> : null}
            >
              {allocating
                ? 'Allocating…'
                : `Assign Route to ${selectedRider?.f_name || 'Rider'}`}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default OrderMapSelector;
